/**
 * Pure helpers for the agentic-network *trigger mode* — how a customer pool
 * is allowed to start a run.
 *
 * The mode is a release-time choice (set in the ReleaseNetworkDialog) that
 * splits the human/automation surface into three industry-standard buckets:
 *
 *   - HEADLESS   : run is initiated only via API (Jira webhook, cron, CI). The
 *                  prompt box in the room stays read-only at IDLE — humans
 *                  show up only on AWAITING_APPROVAL (email + room).
 *   - HYBRID     : the API is open AND a CREATOR can start a run from the
 *                  prompt box. Default — covers most enterprise pools.
 *   - MANUAL     : prompt-box only. The API still exists but rejects
 *                  unattended invocations (anti-cron-foot-gun).
 *
 * We carry it as a plain string on the wire ("HEADLESS" / "HYBRID" / "MANUAL")
 * so future modes (e.g. SCHEDULED, OMNICHANNEL) slot in without a schema
 * migration. The two boolean projections below (``allowsApi`` / ``allowsManual``)
 * are what the UI actually gates on, so flipping a mode never re-renders the
 * whole composer if the projection didn't change.
 *
 * Kept pure (no React, no I/O) so the backend release handler can lift the
 * same constants once the persistence layer is updated in Phase B.
 */

export const TRIGGER_HEADLESS = 'HEADLESS';
export const TRIGGER_HYBRID = 'HYBRID';
export const TRIGGER_MANUAL = 'MANUAL';

export const TRIGGER_MODES = Object.freeze([
  TRIGGER_HEADLESS,
  TRIGGER_HYBRID,
  TRIGGER_MANUAL,
]);

export const DEFAULT_TRIGGER_MODE = TRIGGER_HYBRID;

/**
 * Coerce arbitrary input into a known trigger constant. Falls back to
 * ``DEFAULT_TRIGGER_MODE`` when the value is empty or unrecognized — the UI
 * layer never wants to render an "unknown" radio that rejects user input
 * after a benign rename.
 *
 * @param {unknown} value
 * @returns {'HEADLESS' | 'HYBRID' | 'MANUAL'}
 */
export function normalizeTriggerMode(value) {
  if (value == null) return DEFAULT_TRIGGER_MODE;
  const v = String(value).trim().toUpperCase();
  if (TRIGGER_MODES.includes(v)) return /** @type {'HEADLESS' | 'HYBRID' | 'MANUAL'} */ (v);
  return DEFAULT_TRIGGER_MODE;
}

/**
 * @param {unknown} mode
 * @returns {boolean}
 */
export function triggerModeAllowsApi(mode) {
  const m = normalizeTriggerMode(mode);
  return m === TRIGGER_HEADLESS || m === TRIGGER_HYBRID;
}

/**
 * @param {unknown} mode
 * @returns {boolean}
 */
export function triggerModeAllowsManual(mode) {
  const m = normalizeTriggerMode(mode);
  return m === TRIGGER_HYBRID || m === TRIGGER_MANUAL;
}

/**
 * Human-readable label + description used by the release dialog and the
 * pool members drawer's read-only mode card. Centralised so two surfaces
 * never disagree on copy.
 *
 * @param {unknown} mode
 * @returns {{ value: string, label: string, description: string }}
 */
export function describeTriggerMode(mode) {
  const m = normalizeTriggerMode(mode);
  if (m === TRIGGER_HEADLESS) {
    return {
      value: TRIGGER_HEADLESS,
      label: 'Headless',
      description:
        'API only. External systems (webhooks, cron, CI) start runs. Humans only join when an approval is requested (email + room).',
    };
  }
  if (m === TRIGGER_MANUAL) {
    return {
      value: TRIGGER_MANUAL,
      label: 'Manual',
      description:
        'Prompt box only. A network supervisor in this room initiates every run; programmatic API calls are rejected.',
    };
  }
  return {
    value: TRIGGER_HYBRID,
    label: 'Hybrid',
    description:
      'API and prompt box. External systems can start runs and a network supervisor can also start one from this room.',
  };
}

/**
 * Iterable used by the release dialog to render the radio group.
 *
 * @returns {Array<{ value: string, label: string, description: string }>}
 */
export function listTriggerModes() {
  return TRIGGER_MODES.map((m) => describeTriggerMode(m));
}
